import { useState, useEffect, useMemo } from 'react';
import { fetchPaymentPlanDetail, recordInstallmentPayment } from '../services/api';
import './RecordInstallmentModal.css';

const PAYMENT_METHODS = [
  { value: 'cash',         label: 'Cash' },
  { value: 'check',        label: 'Check' },
  { value: 'card',         label: 'Credit Card' },
  { value: 'money_order',  label: 'Money Order' },
  { value: 'online',       label: 'Online / Portal' },
];

const FREQ_LABELS = { weekly: 'Weekly', bi_weekly: 'Bi-Weekly', monthly: 'Monthly' };
const STATUS_COLORS = {
  pending: { bg: '#eff6ff', color: '#1d4ed8' },
  partial: { bg: '#fff7ed', color: '#c2410c' },
  paid:    { bg: '#f0fdf4', color: '#15803d' },
  missed:  { bg: '#fef2f2', color: '#dc2626' },
};

function fmt(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function RecordInstallmentModal({ planId, onClose, onSaved }) {
  const [detail, setDetail]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [selectedInstId, setSelectedInstId] = useState(null);
  const [form, setForm] = useState({
    payment_date:    today(),
    amount:          '',
    payment_method:  'check',
    reference_number: '',
    notes:           '',
  });
  const [allocations, setAllocations] = useState({}); // claim_id → amount string

  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPaymentPlanDetail(planId)
      .then((res) => {
        setDetail(res.data);
        // Auto-select the next pending/partial installment
        const next = res.data.installments.find((i) => i.status === 'pending' || i.status === 'partial');
        if (next) {
          setSelectedInstId(next.installment_id);
          const remaining = parseFloat(next.amount_due) - parseFloat(next.amount_paid);
          setForm((f) => ({ ...f, amount: remaining.toFixed(2) }));
        }
        // Default allocation: spread evenly across claims with remaining balance
        const claimAllocs = {};
        res.data.claimAllocations.forEach((ca) => {
          claimAllocs[ca.claim_id] = '';
        });
        setAllocations(claimAllocs);
      })
      .catch(() => setLoadError('Failed to load plan details.'))
      .finally(() => setLoading(false));
  }, [planId]);

  const selectedInst = useMemo(() => {
    if (!detail) return null;
    return detail.installments.find((i) => i.installment_id === selectedInstId) ?? null;
  }, [detail, selectedInstId]);

  const payAmount     = parseFloat(form.amount) || 0;
  const allocTotal    = Object.values(allocations).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const allocMismatch = Math.abs(allocTotal - payAmount) > 0.02 && payAmount > 0;

  const handleAllocateEvenly = () => {
    if (!detail || payAmount <= 0) return;
    const eligible = detail.claimAllocations.filter((ca) => parseFloat(ca.remaining) > 0);
    if (!eligible.length) return;
    const perClaim = payAmount / eligible.length;
    const next = {};
    detail.claimAllocations.forEach((ca) => {
      next[ca.claim_id] = parseFloat(ca.remaining) > 0 ? perClaim.toFixed(2) : '0';
    });
    setAllocations(next);
  };

  const handleInstallmentChange = (inst) => {
    setSelectedInstId(inst.installment_id);
    const remaining = parseFloat(inst.amount_due) - parseFloat(inst.amount_paid);
    setForm((f) => ({ ...f, amount: remaining.toFixed(2) }));
    setAllocations((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => { next[k] = ''; });
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    if (!selectedInstId) { setError('Select an installment.'); return; }
    if (payAmount <= 0)   { setError('Enter a valid payment amount.'); return; }
    if (allocMismatch)    { setError('Allocation total must equal payment amount.'); return; }

    const allocsList = Object.entries(allocations)
      .map(([claim_id, amt]) => ({ claim_id: parseInt(claim_id, 10), amount: parseFloat(amt) || 0 }))
      .filter((a) => a.amount > 0);

    if (!allocsList.length) { setError('Allocate the payment to at least one claim.'); return; }

    setSaving(true);
    try {
      await recordInstallmentPayment(planId, selectedInstId, {
        payment_date:    form.payment_date,
        amount:          payAmount,
        payment_method:  form.payment_method,
        reference_number: form.reference_number || null,
        notes:           form.notes || null,
        allocations:     allocsList,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rim-overlay">
      <div className="rim-panel">

        {/* Header */}
        <div className="rim-header">
          <div>
            <h2 className="rim-title">Record Installment Payment</h2>
            {detail && <p className="rim-sub">{detail.plan.patient_name} — {detail.plan.plan_number}</p>}
          </div>
          <button className="rim-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="rim-body">

          {loading && <div className="rim-loading"><div className="rim-spinner" /><span>Loading plan…</span></div>}
          {loadError && <div className="rim-error">{loadError}</div>}

          {!loading && !loadError && detail && !success && (
            <>
              {/* Plan summary */}
              <div className="rim-plan-summary">
                <div className="rim-ps-item">
                  <span className="rim-ps-label">Plan Total</span>
                  <span className="rim-ps-val">{fmt(detail.plan.total_amount)}</span>
                </div>
                <div className="rim-ps-item">
                  <span className="rim-ps-label">Paid</span>
                  <span className="rim-ps-val" style={{ color: '#15803d' }}>{fmt(detail.plan.amount_paid)}</span>
                </div>
                <div className="rim-ps-item">
                  <span className="rim-ps-label">Remaining</span>
                  <span className="rim-ps-val" style={{ color: '#171363', fontWeight: 700 }}>{fmt(detail.plan.remaining_balance)}</span>
                </div>
                <div className="rim-ps-item">
                  <span className="rim-ps-label">Frequency</span>
                  <span className="rim-ps-val">{FREQ_LABELS[detail.plan.frequency]}</span>
                </div>
              </div>

              {/* Installment selector */}
              <div className="rim-section-title">Select Installment</div>
              <div className="rim-inst-list">
                {detail.installments.map((inst) => {
                  const isPaid   = inst.status === 'paid';
                  const selected = inst.installment_id === selectedInstId;
                  const style    = STATUS_COLORS[inst.status] ?? STATUS_COLORS.pending;
                  return (
                    <button
                      key={inst.installment_id}
                      className={`rim-inst-row ${selected ? 'selected' : ''} ${isPaid ? 'paid' : ''}`}
                      onClick={() => !isPaid && handleInstallmentChange(inst)}
                      disabled={isPaid}
                    >
                      <span className="rim-inst-num">#{inst.installment_number}</span>
                      <span className="rim-inst-date">{fmtDate(inst.due_date)}</span>
                      <span className="rim-inst-due">{fmt(parseFloat(inst.amount_due) - parseFloat(inst.amount_paid))} remaining</span>
                      <span
                        className="rim-inst-status"
                        style={{ background: style.bg, color: style.color }}
                      >
                        {inst.status.charAt(0).toUpperCase() + inst.status.slice(1)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Payment form */}
              {selectedInst && (
                <>
                  <div className="rim-section-title" style={{ marginTop: 20 }}>Payment Details</div>
                  <div className="rim-form-grid">
                    <div className="rim-field">
                      <label className="rim-label">Payment Date</label>
                      <input
                        className="rim-input"
                        type="date"
                        value={form.payment_date}
                        onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                      />
                    </div>
                    <div className="rim-field">
                      <label className="rim-label">Amount</label>
                      <div className="rim-money-wrap">
                        <span className="rim-money-sym">$</span>
                        <input
                          className="rim-input rim-money-input"
                          type="number"
                          min="0.01"
                          step="0.01"
                          placeholder="0.00"
                          value={form.amount}
                          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rim-field rim-field-full">
                    <label className="rim-label">Payment Method</label>
                    <div className="rim-method-pills">
                      {PAYMENT_METHODS.map((m) => (
                        <button
                          key={m.value}
                          className={`rim-method-pill ${form.payment_method === m.value ? 'active' : ''}`}
                          onClick={() => setForm((f) => ({ ...f, payment_method: m.value }))}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rim-field rim-field-full">
                    <label className="rim-label">Reference Number</label>
                    <input
                      className="rim-input"
                      type="text"
                      placeholder="Check #, auth code, etc. (optional)"
                      value={form.reference_number}
                      onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))}
                    />
                  </div>

                  {/* Claim allocation */}
                  <div className="rim-section-title" style={{ marginTop: 20 }}>
                    Apply to Claims
                    <button className="rim-alloc-even-btn" onClick={handleAllocateEvenly}>Allocate evenly</button>
                  </div>
                  <div className="rim-alloc-table-wrap">
                    <table className="rim-alloc-table">
                      <thead>
                        <tr>
                          <th>Claim #</th>
                          <th>Svc Date</th>
                          <th className="r">Allocated</th>
                          <th className="r">Remaining</th>
                          <th className="r">Apply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.claimAllocations.map((ca) => (
                          <tr key={ca.claim_id}>
                            <td className="rim-claim-num">{ca.claim_number}</td>
                            <td>{fmtDate(ca.service_date)}</td>
                            <td className="r">{fmt(ca.allocated_amount)}</td>
                            <td className="r" style={{ color: parseFloat(ca.remaining) > 0 ? '#171363' : '#9ca3af', fontWeight: 600 }}>
                              {fmt(ca.remaining)}
                            </td>
                            <td className="r">
                              <div className="rim-apply-wrap">
                                <span className="rim-apply-sym">$</span>
                                <input
                                  className="rim-apply-input"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={allocations[ca.claim_id] ?? ''}
                                  onChange={(e) => setAllocations((a) => ({ ...a, [ca.claim_id]: e.target.value }))}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Allocation balance indicator */}
                  <div className={`rim-alloc-balance ${allocMismatch ? 'mismatch' : 'ok'}`}>
                    <span>Allocated:</span>
                    <strong>{fmt(allocTotal)}</strong>
                    <span>of</span>
                    <strong>{fmt(payAmount)}</strong>
                    {allocMismatch
                      ? <span className="rim-alloc-warn">— must match payment amount</span>
                      : allocTotal > 0 && <span className="rim-alloc-ok">✓</span>
                    }
                  </div>

                  <div className="rim-field rim-field-full" style={{ marginTop: 14 }}>
                    <label className="rim-label">Notes</label>
                    <textarea
                      className="rim-textarea"
                      rows="2"
                      placeholder="Optional"
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {error && <div className="rim-error" style={{ marginTop: 12 }}>{error}</div>}
            </>
          )}

          {/* Success */}
          {success && (
            <div className="rim-success">
              <div className="rim-success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <h3 className="rim-success-title">Payment Recorded</h3>
              <p className="rim-success-msg">
                Installment #{selectedInst?.installment_number} — {fmt(form.amount)} posted
              </p>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="rim-footer">
          {success ? (
            <button className="rim-btn-primary" onClick={() => { onSaved?.(); onClose(); }}>Done</button>
          ) : (
            <>
              <button className="rim-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button
                className="rim-btn-primary"
                onClick={handleSubmit}
                disabled={saving || loading || !selectedInstId}
              >
                {saving ? 'Posting…' : 'Post Payment'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
